import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import { push, replace } from 'redux-first-history';

import { createThunkRequest, DELETE, GET, POST, PUT } from '@webapp/account/src/libs/request';
import { selectLicenseInfo, selectProfileId } from '@webapp/account/src/resources/profile/selectors';
import { upsertQuestions } from '@webapp/account/src/resources/reports/async';
import { AddonList, initialAddons } from '@webapp/account/src/resources/survey/addons/addons';

import { DATE_FORMAT, FILTER_DATE_FORMAT, today } from '@webapp/common/lib/date';
import { GOALS, reachGoal } from '@webapp/common/lib/metrics';
import { serializeParams } from '@webapp/common/lib/params';
import { LocalStorage, SessionStorage } from '@webapp/common/lib/storage';
import { ListPos } from '@webapp/common/lib/ui';
import { undefDestroy } from '@webapp/common/lib/utils';
import { CompletePageType, QuestionType } from '@webapp/common/resources/survey';

import { getFilterParams } from '../lib';
import { NEW_PAGE_NAME } from '../pages';
import { AccountRoutes } from '../routes';
import type { States } from '../states';

import {
    ChartType,
    mapChartBrowser,
    mapChartDate,
    mapChartDevice,
    mapChartOs,
    mapChartStat,
    mapChartUri,
} from './chart';
import type { StatFilter } from './lib';
import { mapFolder, mapPasswords, mapSurvey, reverseMapSurvey } from './map';
import { selectExpandedFolderId, selectSurveyById, selectSurveyCount, selectSurveyList } from './selectors';
import { initialSurvey } from './survey';

export const folderAdd = createThunkRequest(
    'survey/folderAdd',
    async ({ name, parentId, after }: { name: string; parentId?: number; after?: number }) => {
        name = name || 'Новая папка';
        const { id } = await POST(
            'add/folder',
            undefDestroy<any>({
                object: 'survey_folder',
                name,
                after,
                parent: parentId || null,
            }),
        );

        if (after) {
            await POST('change/folder', { id, after });
        }

        return {
            id,
            parentId,
            name,
            after,
        };
    },
);

export const getFileToken = createThunkRequest(
    'survey/getFileToken',
    async ({ fileId }: { fileId: number }) => {
        const { link } = await POST('get/file_token', {
            id: fileId,
        });

        return { link };
    },
    { notify: false },
);

export const removeSurvey = createThunkRequest(
    'survey/remove',
    async (
        {
            surveyId,
            toList = false,
        }: {
            surveyId: number;
            toList: boolean;
        },
        { dispatch },
    ) => {
        const { message } = await POST('delete/survey', {
            id: surveyId,
        });

        if (toList) {
            dispatch(push(AccountRoutes.account.link));
        }

        return {
            message,
            surveyId,
        };
    },
);

export const answersRemove = createThunkRequest(
    'survey/answersRemove',

    async ({ surveyId }: { surveyId: number }) => {
        const { message } = await POST('delete/survey_response', {
            survey: surveyId,
        });

        return {
            message,
            surveyId,
        };
    },
);

export const subdomainChange = createThunkRequest(
    'survey/subdomainChange',
    async ({ surveyId, subdomain }: { surveyId: number; subdomain: string }) => {
        const { message, rewrite } = await POST('change/rewrite', {
            survey: surveyId,
            rewrite: subdomain,
        });

        return {
            message,
            surveyId,
            subdomain: rewrite,
        };
    },
);

export const copyTemplate = createThunkRequest(
    'survey/copyTemplate',
    async ({ templateId }: { templateId: number }, { dispatch }) => {
        const { id } = await POST('copy/template', {
            id: templateId,
        });

        dispatch(push(AccountRoutes.survey.create(id)));

        return {
            id: Number(id),
        };
    },
    { notify: false },
);

export const moveToFolder = createThunkRequest(
    'survey/moveToFolder',
    async ({
        surveyId,
        folderId,
        overId,
        pos,
    }: {
        surveyId: number;
        folderId: number;
        overId?: number;
        pos?: string;
    }) => {
        // TODO call saveSurvey action
        const data = {
            id: surveyId,
            folder_id: folderId || null,
        };

        if (pos && overId) {
            data[pos] = overId;
        }

        await POST('change/survey', data);

        return {
            message: 'Перемещено',
            folderId,
            surveyId,
        };
    },
);

export const copySurvey = createThunkRequest(
    'survey/copy',
    async (
        {
            surveyId,
            toSurvey = false,
        }: {
            surveyId: number;
            toSurvey: boolean;
        },
        { dispatch, getState },
    ) => {
        const survey = selectSurveyById(surveyId)(getState());
        const newName = survey.name + ' (копия)';
        const { id, message } = await POST('copy/survey', {
            id: surveyId,
            title: newName,
        });

        if (toSurvey) {
            dispatch(push(AccountRoutes.survey.create(id)));
        }

        return {
            message,
            survey: {
                ...survey,
                id: Number(id),
                name: newName,
            },
        };
    },
);

export const getStat = createThunkRequest(
    'survey/stat',
    async ({ surveyId, filter }: { surveyId: number; filter: StatFilter }) => {
        const { chartGrouping, from, to } = filter;
        const start = dayjs(from);
        const end = dayjs(to);
        const now = dayjs().format(FILTER_DATE_FORMAT);
        const {
            browser,
            date,
            device,
            os,
            source,
            stat: [stat],
        } = await POST('get/stat_response', {
            survey: surveyId,
            group: chartGrouping,
            start: start.isValid() ? start.format(FILTER_DATE_FORMAT) : now,
            end: end.isValid() ? end.format(FILTER_DATE_FORMAT) : now,
        });

        // TODO don't mix ui and data
        return {
            chartData: mapChartStat(stat),
            charts: [
                {
                    name: 'Ответов за выбранный период',
                    type: ChartType.AREA,
                    legends: date.map(mapChartDate),
                },
                {
                    name: 'Источник распространения',
                    type: ChartType.RADIAL,
                    legends: source.map(mapChartUri),
                },
                {
                    name: 'Операционная система',
                    type: ChartType.LINE,
                    legends: os.map(mapChartOs),
                },
                {
                    name: 'Браузер',
                    type: ChartType.PERCENT,
                    legends: browser.map(mapChartBrowser),
                },
                {
                    name: 'Устройство',
                    type: ChartType.PIE,
                    legends: device.map(mapChartDevice),
                },
            ],
        };
    },
    { notify: false },
);

export const saveSurvey = createThunkRequest(
    'survey/save',
    async ({
        key,
        surveyId,
        value,
    }: {
        surveyId: number;
        key: string;
        value: string | number | boolean | Array<any>;
    }) => {
        const req: any = {
            [key]: value,
        };
        const { message } = await POST('change/survey', {
            id: surveyId,
            ...reverseMapSurvey(req),
        });

        return {
            message,
            surveyId,
            key,
            value,
        };
    },
);

export const getAnketa = createThunkRequest(
    'survey/getAnketa',
    async ({ surveyId }: { surveyId: number }) => {
        const { file } = await POST('get/anketa', {
            survey: surveyId,
        });
        window.open(file, 'blank');
        return { file };
    },
    { notify: false },
);

export const getSurvey = createThunkRequest(
    'survey/getSurvey',
    async ({ surveyId }: { surveyId: number }) => {
        const { rows, status } = await POST('show/survey', {
            id: surveyId,
        });

        const survey = { ...mapSurvey(rows) };

        const { dateFrom, dateTo, comparePeriods } =
            SessionStorage.get<{ dateFrom: string; dateTo: string; comparePeriods: Array<any> }>(
                `survey_${survey.id}_date_filters`,
            ) || {};

        survey.addons.filters.dateFrom = dateFrom || dayjs(survey.createdDatetime.split(' ')[0]).format(DATE_FORMAT);
        survey.addons.filters.dateTo = dateTo || today;
        survey.addons.filters.comparePeriods = Array.isArray(comparePeriods) ? comparePeriods : [];

        return {
            survey,
            surveyId,
            status,
        };
    },
    { notify: false },
);

export const refreshSurvey = createThunkRequest(
    'survey/refreshSurvey',
    async ({ surveyId }: { surveyId: number }) => {
        const { rows } = await POST('show/survey', {
            id: surveyId,
        });

        return {
            survey: mapSurvey(rows),
            surveyId,
        };
    },
    { notify: false },
);

export const getSurveyPromocodes = createThunkRequest(
    'survey/getSurveyPromocodes',
    async ({ surveyId }: { surveyId: number }) => {
        const { promo_codes: promocodes } = await POST('get/promo_codes', {
            survey: surveyId,
        });

        return {
            promocodes,
            surveyId,
        };
    },
    { notify: false },
);

export const getSurveyList = createThunkRequest(
    'survey/getSurveyList',
    async (
        {
            upsert = false,
            reset,
        }: {
            upsert: boolean;
            reset?: boolean;
        },
        { getState },
    ) => {
        const { rows } = await POST('list/survey');
        const surveyList = selectSurveyList(getState());

        return {
            items: rows.map((r) => {
                r = mapSurvey(r, upsert);

                if (reset) {
                    return r;
                }

                // TODO: избавиться от этого условия
                const existing = surveyList.find(({ id }) => id === r.id);
                return existing ? existing : r;
                // return r;
            }),
        };
    },
    { notify: false },
);

export const addIndividualPasswords = createThunkRequest(
    'survey/addIndividualPasswords',
    async ({ surveyId, passwords }: { surveyId: number; passwords: Array<string> }) => {
        const { message } = await POST('add/individual_password', {
            survey: surveyId,
            data: JSON.stringify(passwords),
        });

        return {
            message,
            surveyId,
            passwords,
        };
    },
);

export const getPersonalPasswords = createThunkRequest(
    'survey/getPersonalPasswords',
    async ({ surveyId }: { surveyId: number }) => {
        const { rows } = await POST('get/individual_password', {
            survey: surveyId,
        });

        const passwords = mapPasswords(rows);

        return {
            surveyId,
            passwords,
        };
    },
    { notify: false },
);

export const addPromocodes = createThunkRequest(
    'survey/addPromocodes',
    async ({ surveyId, promocodes }: { surveyId: number; promocodes: Array<string> }) => {
        await POST('change/promo_codes', {
            survey: surveyId,
            data: JSON.stringify(promocodes),
        });

        return {
            surveyId,
            promocodes,
            message: 'Промокоды успешно загружены',
        };
    },
    { notifyError: 'Произошла ошибка при загрузке промокодов' },
);

export const deleteUserFiles = createThunkRequest(
    'survey/deleteUserFiles',
    async (
        {
            surveyId,
            questionId,
            audio,
            withFilters,
        }: {
            surveyId: number;
            questionId: number;
            audio: boolean;
            withFilters: boolean;
        },
        { dispatch, getState },
    ) => {
        const { globalFilters, reports } = getState().Reports;
        const filterParams = withFilters ? getFilterParams(globalFilters, reports) : {};
        const req = cloneDeep(filterParams);

        if (questionId) {
            req.questionId = questionId;
        } else if (surveyId) {
            req.surveyId = surveyId;
        }

        req.audio = audio;

        const { message } = await POST('delete/response_files', req);

        if (questionId) {
            const report = reports.find(({ id }) => id === questionId);
            await dispatch(upsertQuestions({ reportId: report.id }));
        } else if (surveyId) {
            const fileReports = reports.filter(({ type }) => type === QuestionType.FILE_UPLOAD).map((r) => r.id);
            await dispatch(getSurvey({ surveyId }));
            await dispatch(upsertQuestions({ reportId: fileReports }));
        }

        return { message };
    },
    { notifyError: 'Не удалось удалить файлы' },
);

export const folderRemove = createThunkRequest(
    'survey/folderRemove',

    async ({ folderId }: { folderId: number }) => {
        await POST('delete/object', {
            object: 'survey_folder',
            id: folderId,
        });

        return {
            folderId,
        };
    },
);

export const licenseApply = createThunkRequest(
    'survey/licenseApply',

    async ({ surveyId }: { surveyId: number }) => {
        const { message } = await POST('activate/license', {
            survey: surveyId,
        });

        return {
            message,
            surveyId,
        };
    },
);

export const addonSave = createThunkRequest(
    'survey/addonSave',
    async (
        {
            surveyId,
            name,
            key,
            value,
        }: {
            surveyId: number;
            name: AddonList;
            key: string;
            value: string | number | boolean | Array<any> | AnyObject;
        },
        { dispatch, getState },
    ) => {
        const { addons } = selectSurveyById(surveyId)(getState());

        let req;

        // TODO refactor
        if (name === AddonList.NOTIFY) {
            req = value;
        } else if (key) {
            req = {
                ...addons[name],
                [key]: value,
            };
        } else {
            req = { ...addons[name], ...(value as AnyObject) };
        }

        if (name === AddonList.FILTERS && (req.dateFrom || req.dateTo)) {
            // TODO provide via action
            SessionStorage.set(`survey_${surveyId}_date_filters`, {
                dateFrom: req.dateFrom,
                dateTo: req.dateTo,
                comparePeriods: req.comparePeriods || [],
            });
        }

        if (name === AddonList.FILTERS) {
            req.excludedParamIds = Array.from(req.excludedParamIds || []);
            req.excludedQuestionIds = Array.from(req.excludedQuestionIds || []);
            req.excludedCrossTabsId = Array.from(req.excludedCrossTabsId || []);
        }

        delete req.addons;
        delete req.chartData;
        delete req.charts;
        delete req.dateFrom;
        delete req.dateTo;
        delete req.comparePeriods;

        const { message, status } = await POST('change/param', {
            object: 'survey_param',
            objectid: surveyId,
            name,
            value: JSON.stringify(req),
        });

        if (value === CompletePageType.TO_SURVEY) {
            await dispatch(getSurveyList({ upsert: true }));
        }

        return {
            message,
            status,
        };
    },
);

export const addSurvey = createThunkRequest(
    'survey/addSurvey',
    async (
        {
            name,
            folderId,
            activateLicense = false,
        }: {
            name: string;
            folderId?: number;
            activateLicense: boolean;
        },
        { dispatch, getState },
    ) => {
        const state = getState() as States;
        const params = Object.entries(serializeParams(initialAddons)).reduce(
            (acc, [name, value]) => (
                acc.push({
                    name,
                    value: cloneDeep(value),
                }),
                acc
            ),
            [],
        );

        const { id: surveyId, message } = await POST('add/survey', {
            title: name,
            name,
            folder_id: folderId || null,
            activate_license: activateLicense ? 1 : 0,
            password: initialSurvey.password,
            params: JSON.stringify(params),
        });

        // Демо-опрос: первый опрос на демо-лицензии. Флаг в localStorage
        // защищает от повторной цели, если демо-опрос удалят и создадут заново.
        const profileId = selectProfileId(state);
        const demoGoalKey = `ym_goal_demo_${profileId}` as const;

        if (
            profileId &&
            selectLicenseInfo(state).isDemo &&
            selectSurveyCount(state) === 0 &&
            !LocalStorage.get(demoGoalKey)
        ) {
            LocalStorage.set(demoGoalKey, true);
            reachGoal(GOALS.demoSurveyCreated, { survey_id: surveyId });
        }

        await POST('add/page', {
            survey: surveyId,
            title: NEW_PAGE_NAME,
        });

        await dispatch(getSurvey({ surveyId }));

        dispatch(replace(AccountRoutes.surveyEditCreate.create(surveyId)));

        return {
            message,
            survey: {
                id: surveyId,
                name,
            },
            activateLicense,
        };
    },
);

export const sortSurvey = createThunkRequest(
    'survey/sort',
    async (
        {
            surveyId,
            overId,
            pos,
        }: {
            surveyId: number;
            overId: number;
            pos: ListPos;
        },
        { getState },
    ) => {
        const folderId = selectExpandedFolderId(getState());
        const req: JsonValue = {
            folder_id: folderId || null,
        };

        switch (pos) {
            case ListPos.AFTER:
                req.after = overId;
                break;
            case ListPos.BEFORE:
            default:
                req.before = overId;
                break;
        }

        if (pos !== ListPos.SAME) {
            // TODO call saveSurvey action
            const { message } = await POST('change/survey', {
                id: surveyId,
                ...req,
            });
            return {
                message,
            };
        }

        return {};
    },
);

export const folderMove = createThunkRequest(
    'survey/folderMove',
    async ({ folderId, overId, pos }: { folderId: number; overId?: number; pos: ListPos }) => {
        const body: any = {
            id: folderId,
        };

        switch (pos) {
            case ListPos.AFTER:
                body.after = overId;
                break;
            case ListPos.BEFORE:
                body.before = overId;
                break;
            default:
        }

        const { message } = await POST('change/folder', body);

        return { message };
    },
);

export const folderSave = createThunkRequest(
    'survey/folderSave',
    async ({ folderId, name }: { folderId: number; name: string }) => {
        const { message } = await POST('change/folder', {
            object: 'survey_folder',
            id: folderId,
            name,
        });

        return {
            folderId,
            message,
            name,
        };
    },
);

export const folderListGet = createThunkRequest(
    'survey/folderListGet',
    async () => {
        const { rows } = await POST('show/folder');

        const folders = rows
            .filter((f) => f.PARENT === null)
            .map(mapFolder)
            .sort((a, b) => a.order - b.order)
            .map((f) => ({
                ...f,
                children: rows
                    .filter((r) => r.PARENT === f.id)
                    .map(mapFolder)
                    .sort((a, b) => a.order - b.order),
            }));

        return {
            folders,
        };
    },
    { notify: false },
);

export const getShareLinks = createThunkRequest(
    'survey/getShareLinks',
    async (surveyId: number) => {
        const { shareLinks } = await GET('get/share_links', {
            surveyId: surveyId,
        });

        return {
            shareLinks,
            surveyId,
        };
    },
    { notify: false },
);

export const addShareLink = createThunkRequest(
    'survey/addShareLink',
    async (data: Record<string, any>) => {
        return await POST('add/share_link', data);
    },
    { notify: false },
);

export const changeShareLink = createThunkRequest(
    'survey/changeShareLink',
    async (data: Record<string, any>) => {
        return await PUT('change/share_link', data);
    },
    { notify: false },
);

export const deleteShareLink = createThunkRequest(
    'survey/deleteShareLink',
    async (id: number) => {
        await DELETE(`delete/share_link/${id}`);

        return {
            id,
        };
    },
    { notify: false },
);
